import {Location} from 'Common/Location';
import {InvalidFieldValueError} from 'Browser/InvalidFieldValueError';
import {palette} from 'Shared/artists/Palette';
import {Setter,onMount,Show,For,createSignal,createMemo,untrack, onCleanup} from 'solid-js';
import { HtmlEditorWidget } from 'Browser/HtmlEditorWidget';
import { css } from '@emotion/css';
import { useUrls } from 'Shared/SiteProvider';
import { IStoreWrap, useStore } from 'Shared/StoreProvider';
import { dbPatchField } from 'Shared/forms/BaseOperations';
import { splitLocation, subdoc } from 'Common/ViewUtils';
import { updateFieldAndSubdoc, patchField, replaceField } from 'Shared/forms/SubdocOps';
import { Select } from '@thisbeyond/solid-select';
import '@thisbeyond/solid-select/style.css';
import Assert from 'Common/Assert';


export const fieldStyle = (narrow:boolean,disable:boolean) => css({
	font: '15px Arial, Helvetica, sans-serif',
	backgroundColor: 'inherit',
	borderTop: 'none',
	borderRight: 'none',
	borderLeft: 'none',
	borderImage: 'initial',
	borderBottom: '1px solid rgb(239, 239, 239)',
	padding: '5px 10px 5px 0',
	width: narrow ? 'unset' : '100%',
	color: disable ? 'rgb(120, 120, 120)' : 'rgb(85, 85, 85)',

	':focus': {
		borderColor: 'rgb(102, 175, 233)',
		outline: 0,
		boxShadow: 'rgba(0, 0, 0, 0.075) 0px 1px 1px inset, rgba(102, 175, 233, 0.6) 0px 0px 8px'
	},
});

export const inputStyle = (narrow:boolean,disable:boolean) => css(fieldStyle(narrow,disable), {
	transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',

	'::placeholder': {
		fontSize: 14,
		fontStyle: 'italic'
	}
});

export const textStyle = (narrow:boolean,disable:boolean) => css(inputStyle(narrow,disable), {
	/* This needs to a bit darker than the default as we need to see it properly for Text components */
	borderBottom: '1px solid rgb(222, 222, 222)',
});

//XXX not currently used
export const textAreaStyle = () => css(fieldStyle(false,false), {
	transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
});

export const selectStyle = (narrow:boolean=true) => css(fieldStyle(narrow,false));

const checkboxStyle = () => css({
	width: 'initial'  //TODO remove after Less gone?
});

const priceStyle = () => css({
	display: 'flex',
	gap: '0.1em',
	alignItems: 'center'
});

export const integerStyle = (disable:boolean) => css(inputStyle(false,disable), {
	width: '6em',
	textAlign: 'right'
});

export const multiSelectStyle = () => css(fieldStyle(false,false),{
	border: 'none',

	'.solid-select-list': {
		fontSize: 12,
		backgroundColor: 'white'
	},
});


export interface IField {
	field:string;
	ref?: HTMLDivElement;
	class?: string;
	disable?:boolean;
	processInput?: (value:string) => any;
}


export interface IText extends IField {
	maxLength?: number;
	narrow?: boolean;
	placeholder?: string;
	onInput?: (value:string) => void;
}

export function Text(P:IText)  
{
	const sw = useStore();

	//TODO if there is an error probably should re-evaluate it every key press...
	//     separate the onChange code? NB annoying when fields complain too soon though.

	const [error,setError] = createSignal(undefined as undefined|string);

	return (<>
		<input type='text'
			id={id(P.field)}
			class={css(textStyle(P.narrow,P.disable),P.class ?? {})} name='' autocomplete='off'
			maxlength={P.maxLength ?? 255}
			value={getValue(sw.location,P.field) ?? ''}
			placeholder={P.placeholder}
			ref={P.ref}
			onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			onInput={e => P.onInput?.(e.target.value)  } 
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
			disabled={P.disable}
		/>
	</>);
}

interface IMultiLineText extends IField {
	numRows:number;
	maxLength?:number;
	onInput?: (value:string) => void;
}

/* XXX if I keep using this, probably want to support paragraphs */
export function MultiLineText(P:IMultiLineText)
{
	const sw = useStore();

	const [error,setError] = createSignal(undefined as undefined|string);

	return (
		<textarea id={id(P.field)}
			ref={P.ref}
			class={inputStyle(false,false)} name='' autocomplete='off'
			rows={P.numRows}
			maxlength={P.maxLength ?? 2000}
			onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			onInput={e => P.onInput?.(e.target.value)  } 
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
		>
			{getValue(sw.location,P.field)}
		</textarea>
	);
}

/* NOTE on subdoc management: see TestFieldsPage.tsx ! */ 

export type ISubdoc = {name?:string|undefined; key?:string; onChange:(newValue:any,oldValue?:string,oldSubdoc?:any) => any|undefined};


interface ISingleSelect extends IField {
	options?: Record<string,string>;
	subdoc?: ISubdoc;
	placeholder?:string;
	wide?:boolean ;

	/* 
		Don't show '(none)'. This is almost not needed - you can use initialValue instead.
		Legacy cases (and maybe others?) sometimes create deep initial trees.
		Note '(none)' will be shown if there is no set value and initialValue and
		defaultValue are not set.
		NOTE defaultValue is incomptable with initialValue and required TODO add typechecking for this or an Assert?
	*/
	required?:boolean;

	/*
		This triggers the auto-creation of subdocs when the component is mounted. 
		XXX the existence of 'initialValue' largely implies 'required'
	*/
	initialValue?:string;
	/*
		Treat null as being equivalent to the specified value.
		WARNING It is assumed that the client code will interpret null as being this value, this value is NOT stored in the DB.
	*/
	defaultValue?:any;
	onChange?: (e: Event) => void;
}

export function SingleSelect(P:ISingleSelect)
{
	const sw = useStore();

	const required = () => P.required || !!P.defaultValue;

	const value = createMemo(() => {
		if (!sw) return undefined;
		if (P.subdoc && P.subdoc.name === undefined)
			return getValue(sw.location,P.field)?.[P.subdoc.key ?? P.field];
		return getValue(sw.location,P.field);
	});

	const options = () => P.options ?? {};

	const [error,setError] = createSignal(undefined as undefined|string);

	onMount(async () => {
		if (P.initialValue !== undefined && untrack(value) === undefined) {
			if (P.subdoc)
				await subdocChange(sw, P.field, P.subdoc, P.initialValue, false);
			else
				await patchField(sw, P.field, P.initialValue);
		}
	});

	return (
		<select id={id(P.field)}
			class={css(selectStyle(!P.wide),P.class ?? {})}
			name=''
			autocomplete='off'
			disabled={P.disable}
			onChange={e => handleChange(sw,e,P,e.target.value=='' ? undefined : e.target.value,setError,false)}
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
		>
			{/*
				Showing '(none)' when 'required' is safer when there is no value than defaulting to
				the first option as the DB won't be updated with the first value unless the user
				changes the selected value.
					In time maybe use JS to remove (none) on first select.
					There is a default option in DbTables which can/should always/sometimes be used			*/}
			<Show when={!required() || (!value() && !P.defaultValue)}>
				<option value='' selected={value()==undefined} >
					{P.placeholder ?? '(none)'}
				</option>
			</Show>

			<For each={Object.entries(options())}>{ item =>
				<option value={item[0]} selected={item[0]==value() || (!value() && item[0]==P.defaultValue)}>
					{item[1]}
				</option>
			}</For>
		</select>
	);
}

interface IBooleanSelect extends IField {
	/* Labels shown for each boolean value */
	options: { false: string; true: string };
	initialValue?: boolean;
	wide?: boolean;
}

/*
	Like SingleSelect but stores a boolean in the DB/store rather than a string.
	The field is omitted from the DB entirely when false, so only 'true' is ever stored
	(a missing field is interpreted as false).
*/
export function BooleanSelect(P:IBooleanSelect)
{
	const sw = useStore();

	const value = () => getValue(sw.location, P.field);

	const current = () => value() ? 'true' : 'false';

	const [error,setError] = createSignal(undefined as undefined|string);

	onMount(async () => {
		if (P.initialValue && untrack(value) === undefined)
			await patchField(sw, P.field, true);
	});

	const onChange = async (e:Event) => {
		const isTrue = (e.target as HTMLSelectElement).value === 'true';
		/* Store true; omit the field (undefined) for false */
		await updateFieldWithValue(sw, P.field, isTrue ? true : undefined, setError);
	};

	return (
		<select id={id(P.field)}
			class={css(selectStyle(!P.wide),P.class ?? {})}
			name=''
			autocomplete='off'
			disabled={P.disable}
			onChange={onChange}
			style={error() ? 'background-color:'+palette.errorBackground : ''}
			data-error={error() ?? ''}
		>
			<option value='false' selected={current()=='false'}>{P.options.false}</option>
			<option value='true' selected={current()=='true'}>{P.options.true}</option>
		</select>
	);
}

interface ICheckbox extends Omit<IField, 'field'> {
	field?: string;
	onChange?: (e: Event) => void;
	checked?: boolean;
	subdoc?: ISubdoc;
	initialValue?: boolean;
}

export function Checkbox(P:ICheckbox)
{
	const sw = useStore();

	const [error,setError] = createSignal(undefined as undefined|string);

	const checked = () => getValue(sw.location, P.field);

	onMount(async () => {
		if (P.initialValue !== undefined && untrack(checked) === undefined)
			await patchField(sw, P.field, P.initialValue);
	});

	return (
		<input type='checkbox' class={checkboxStyle()} name=''
			id={id(P.field ?? '')}
			checked={checked()}
			onChange={e => handleChange(sw,e,P,(e.target as HTMLInputElement).checked,setError,true)}
		/>
	);
}

interface IInteger extends IField {
	min?:number;
	max?:number;
	step?:number|'any';
	placeholder?:string;
	start?:string;
	initialValue?: number;
}

export function Integer(P:IInteger)
{
	const sw = useStore();

	const processInput = (numStr:string|undefined) => {
		if (numStr==undefined) return undefined;
		const num =  Number.parseInt(numStr);
		return P.processInput ? P.processInput(num) : num;
	};

	const value = () => getValue(sw.location,P.field);

	onMount(async () => {
		if (P.initialValue !== undefined && untrack(value) === undefined)
			await patchField(sw, P.field, P.initialValue);
	});

	return (
		<input type='number'
			id={id(P.field)}
			class={css(integerStyle(value()==undefined),P.class ?? {})}
			name='' 
			value={value() ?? P.start}
			max={P.max} min={P.min} step={P.step ?? '1'}
			onChange={e => updateField(e.target,sw,P.field,processInput)}
			disabled={P.disable}
			placeholder={P.placeholder ?? ''}
		/>
	);
}

export function NumberField(P:IInteger)
{
	const sw = useStore();

	const processInput = (numStr:string|undefined) => {
		const num = isNaN(Number(numStr)) ? 0 : Number(numStr);
		return P.processInput ? P.processInput(num) : num;
	};

	return (
		<input type='number'
			id={id(P.field)}
			class={css(integerStyle(false),P.class ?? {})}
			name='' value={getValue(sw.location,P.field)}
			max={P.max} min={P.min} step={P.step ?? '1'}
			onChange={e => updateField(e.target,sw,P.field,processInput)}
			disabled={P.disable}
			placeholder={P.placeholder ?? ''}
		/>
	);
}

interface IMultiSelect extends IField {
	options:Record<string,string>;
}

export function MultiSelect(P:IMultiSelect)
{
	//WARNING there can be z-index problems with the library I'm using here. Hopefully the library will fix it sometime

	const sw = useStore();

	const value = createMemo(() => {
		/* Remove any selected items that aren't currently in the list of options: */
		const options = [];
		for (const key of getValue(sw.location,P.field) ?? [])
			if (P.options[key])
				options.push(key);
		return options;
	}, undefined, { equals: (a, b) => a.length === b.length && a.every((v, i) => v === b[i]) });


	/* Annoyingly Solid Select triggers onChange when initialising, so prevent this from happening */
	let initialising = true;

	const change = value => {
		if (initialising) {
			initialising = false;
			return;
		}
		updateFieldWithValue(sw,P.field,value,null,P.processInput);
	};

	const unselected = () => createMemo(() => {
		const options = {...P.options};
		for (const key of getValue(sw.location,P.field) ?? []) 
			if (P.options[key])
				delete options[key];
		return Object.keys(options);
	});

	//FIXME use the standard focus styling
	return (
		<Select class={P.class ?? multiSelectStyle()} multiple options={unselected()} format={key => P.options[key]}
			onChange={change} initialValue={untrack(value)} placeholder='' disabled={P.disable}
		/>
	);
}

interface IPrice extends IField {
	maxLength?:number;
	placeholder?:string;
}

export function Price(P:IPrice)
{
	const sw = useStore();

	const [error,setError] = createSignal(undefined as undefined|string);

	return (
		<div class={priceStyle()}>
			$ <input type='text' class={inputStyle(false,false)}
				id={id(P.field)}
				name='' autocomplete='off'
				maxlength={P.maxLength ?? 255}
				value={getValue(sw.location,P.field) ?? ''}
				placeholder={P.placeholder ?? 'X.XX'}
				onChange={e => updateField(e.target,sw,P.field,P.processInput,setError)}
			/>
		</div>
	);
}


interface IHtml extends IField { 
	placeholder?: string;
	options;
}

//TODO merge this function with HtmlEditorWidget
export function Html(P:IHtml)
{
	const urls = useUrls();
	const sw = useStore();

	let htmlEditor!:HtmlEditorWidget;
	let htmlNode!: HTMLDivElement;

	onMount(() => {
		htmlEditor = new HtmlEditorWidget(urls,P.options);
		htmlEditor.init(htmlNode)
	});
	onCleanup(() => htmlEditor?.destroy(htmlNode));

	return (
		<div ref={htmlNode} id={id(P.field)} onChange={e => updateField(e.target,sw,P.field,P.processInput)}>
			<div id={`hugerte-${id(P.field)}`} innerHTML={getValue(sw.location,P.field)} placeholder={P.placeholder}></div>
		</div>
	);
}


/* --- Private functions: --- */

async function handleChange(sw,e,P,value,setError,isBooleanKey:boolean)
{
	if (P.subdoc)
		await subdocChange(sw,P.field, P.subdoc, value,isBooleanKey);
	else if (P.onChange)
		P.onChange(e);
	else if (isBooleanKey)
		await updateFieldWithValue(sw,P.field, value, setError);
	else
		await updateField(e.target,sw,P.field,P.processInput,setError);
}

async function subdocChange(sw, field: string, ms: ISubdoc, value: any,isBooleanKey:boolean)
{
console.log('Inputs subdocChange() - 0 ms:',ms);		

	if (ms.name === undefined) {
		const old = getValue2(sw, field);
		let newSubdoc;

		if (isBooleanKey) {
			/* Checkboxes etc don't required a key field */
			const computed = ms.onChange(value, old!=null, old);
			newSubdoc = computed !== undefined ? computed : undefined;
		} else {
			const keyField = Assert.have(ms.key);
			const computed = ms.onChange(value, old?.[keyField], old);
			newSubdoc = computed !== undefined ? {...computed, [keyField]: value} : undefined;
		}
		if (newSubdoc !== old)
			await replaceField(sw, field, newSubdoc);
	}
	else {
		const oldValue = getValue2(sw, field);
		const old = subdoc(sw.store, [...sw.location, ms.name]);
		const newSubdoc = ms.onChange(value, oldValue, old);

		if (newSubdoc !== old)
			await updateFieldAndSubdoc(sw, field, value, ms.name, newSubdoc);
		else
			await patchField(sw, field, value);
	}
}

export function id(field:string)
{
	const sw = useStore();
	return sw.permission+'-'+JSON.stringify(sw.location ?? [])+'-'+field;
}

export function getValue(location:Location,field:string)
{
	const sw = useStore();

	let pos = sw.store;
	for (const part of location ?? []) 
		pos = pos[part];

	return pos==undefined ? undefined : pos[field];
}

export function getValue2(sw,field:string)
{
	let pos = sw.store;
	for (const part of sw.location ?? []) 
		pos = pos[part];

	return pos==undefined ? undefined : pos[field];
}

export async function updateField<D>(node:HTMLElement,sw:IStoreWrap<D>,field:string,processInput:((value:string)=>unknown)|undefined,setError?:Setter<string|undefined>)
{
//TODO add support for a custom 'update' callback to be used instead (eg see ColourPicker.tsx code).


	/* Swap whitespace strings with undefined */
	Assert.property('value',node);
	const val1 = (node as any).value.trim();
	const val = val1=='' ? undefined : val1;

	let value: unknown;
	try {
		value = val!=undefined && processInput ? processInput(val) : val;
	}
	catch(err) {
		if (err instanceof InvalidFieldValueError && setError) {
			setError(err.message);
			return;
		}
		throw err;
	}

	await updateFieldWithValue<D>(sw,field,value,setError);
}

export async function updateFieldWithValue<D>(sw:IStoreWrap<D>,field:string,value:any,setError?:Setter<string|undefined>)
{
	if (setError)
		setError(undefined);

	try {
		let ret;

		/* 
			The awaits here are important - if afterUpdate() contains a DB update then tRPC will batch the operations and this
			can lead to two updates being performed simultaneously on the same doc leading to one DB update effectly disappearing.
		*/

		if (sw.prepareParent)
			ret = await updateParent(sw,field,value);
		else 
			ret = await dbPatchField(sw,field,value);

		if (sw.afterUpdate)
			await sw.afterUpdate(value,ret);
	}
	catch(err) {
		if (err instanceof InvalidFieldValueError && setError)
			setError(err.message);
		else
			throw err;
	}
}

/* Create/destroy the parent node as well as the current field: */
async function updateParent<D>(sw:IStoreWrap<D>,field:string,value:any)
{
	const previous = subdoc(sw.store,sw.location);
	const parentObject = previous ?? await sw.prepareParent?.(field,value);

	//XXX Its somewhat possible (but unlikely) that we may need to support the creation of arrays as well as objects
	const [path,parentLocation] = splitLocation(sw.location);

	/* WARNING using ...sw instead here seems to trigger an update */
	const sw2 = {setStore:sw.setStore,server:sw.server,page:sw.page,permission:sw.permission,docId:sw.docId,location:path};

	let fields = {...parentObject,[field]:value};

	//XXX cf a 'cleanup' option, or automatic removal of parent objects when not used

	return await dbPatchField(sw2,parentLocation,fields);
}

